import { z } from 'zod';
import { AssignTaskInput, ChangeStatusInput, TaskPriority } from './project.js';
/**
 * Lệnh nháp do AI đề xuất — APM-AI-l (`plan-ai-features.md` mục 6).
 *
 * ## Vì sao AI đề xuất **lệnh** chứ không ghi thẳng
 *
 * Luật #6 nói AI chỉ gợi ý, người duyệt. Với một thay đổi (chia nhỏ task) thì
 * "gợi ý" là một bản ghi chờ duyệt. Với **bốn mươi** thay đổi ("dời việc của
 * Minh sang Hà") thì bốn mươi bản ghi chờ duyệt là không ai duyệt nổi.
 *
 * Bản nháp lệnh giải chỗ đó: một hàng mang cả danh sách, người dùng xem diff,
 * bỏ từng dòng, rồi duyệt **một lần**. Lúc áp dụng, mọi lệnh chạy qua CommandBus
 * trong **một** transaction — nên hoặc cả bốn mươi thay đổi cùng có event, hoặc
 * không thay đổi nào xảy ra. Nửa chừng là thứ Replay không dựng lại được.
 *
 * ## Danh sách loại lệnh cố ý HẸP
 *
 * Chỉ những lệnh đổi một thuộc tính của task đang có. Không tạo, không xoá,
 * không đụng dự án hay thành viên. Mở rộng nó là mở rộng thứ AI có thể đề nghị
 * làm hàng loạt, và mỗi loại mới cần một câu trả lời riêng cho "nếu sai thì
 * hoàn tác thế nào".
 */
export const ProposedCommand = z.discriminatedUnion('type', [
    z.object({ type: z.literal('assign_task'), taskId: z.uuid(), input: AssignTaskInput }),
    z.object({ type: z.literal('change_status'), taskId: z.uuid(), input: ChangeStatusInput }),
    z.object({
        type: z.literal('set_priority'),
        taskId: z.uuid(),
        input: z.object({ priority: TaskPriority }),
    }),
    z.object({
        type: z.literal('reschedule_task'),
        taskId: z.uuid(),
        input: z.object({
            startDate: z.string().nullable().optional(),
            dueDate: z.string().nullable().optional(),
        }),
    }),
]);
/**
 * Loại lệnh → quyền cần có. Kiểm ở **cả** lúc đề xuất và lúc áp dụng.
 *
 * Ưu tiên và ngày là **lập kế hoạch** — `project:update`, cùng cửa với
 * `assertMaySchedule` ở `PATCH /tasks/:id`. Để `task:update` thì bản nháp AI
 * là cửa hông cho đúng thứ đường sửa tay đã chặn.
 */
export const COMMAND_PERMISSION = {
    assign_task: 'task:assign',
    change_status: 'task:update',
    set_priority: 'project:update',
    reschedule_task: 'project:update',
};
export const CommandDraftItem = z.object({
    command: ProposedCommand,
    /** Một dòng tiếng Việt cho người duyệt đọc — "Giao TASK-12 cho Hà". */
    humanLine: z.string().min(1).max(300),
    /** Cảnh báo từ `finalize()`; lệnh **không** bị xoá, người duyệt tự quyết. */
    warning: z.string().max(300).nullable(),
});
export const CommandDraftStatus = z.enum(['pending', 'applied', 'cancelled']);
export const CommandDraftView = z.object({
    id: z.uuid(),
    projectId: z.uuid(),
    skill: z.string(),
    status: CommandDraftStatus,
    items: z.array(CommandDraftItem),
    createdAt: z.iso.datetime(),
    appliedAt: z.iso.datetime().nullable(),
    /** Số lệnh đã chạy khi áp dụng — khác `items.length` nếu người dùng bỏ bớt. */
    appliedCount: z.number().int().nonnegative().nullable(),
});
/**
 * Thân của `POST /ai/command-drafts/:id/apply`.
 *
 * Chỉ có **chỉ số bị loại**, không có payload: server dựng lại lệnh từ bản nháp
 * đã lưu. Nhận payload từ client nghĩa là ai đó sửa nó trên đường đi và cửa
 * duyệt chỉ còn là trang trí.
 */
export const ApplyCommandDraftInput = z.object({
    exclude: z.array(z.number().int().nonnegative()).max(200).default([]),
});
/** Output của skill `bulk_edit`. */
export const BulkEditOutput = z.object({
    /**
     * Bản nháp mà `propose_commands` vừa ghi.
     *
     * Model chép lại từ kết quả tool, và grounding check (APM-H-n) xác nhận nó có
     * thật — nên giao diện có thể tin id này để gọi endpoint áp dụng. Không có nó
     * thì client phải đoán bản nháp nào vừa được tạo.
     */
    draftId: z.uuid(),
    items: z.array(CommandDraftItem).min(1).max(100),
    /** Câu tóm tắt cho người duyệt: "40 việc đổi người, 3 việc vượt năng lực". */
    summary: z.string().min(1).max(500),
});
//# sourceMappingURL=ai-command-draft.js.map